// 207C §2 [RULED — Laurent, 2026-09-24, ruling 6] — AN AMOUNT SHOWS ITS
// THOUSANDS WITH A SPACE AND IS TYPED WITH A DECIMAL COMMA.
//
// Laurent: "the quantum field in CCV/Clients (i) has no space with thousands,
// should have, (ii) does not allow fractions (comma and figures after it),
// while should". MEASURED on 0921877c (scripts/drive-207c.js --part=quantum,
// Chromium and WebKit alike): the members-list cell showed the stored number as
// it is — « 92159 », « 76.5 » — and was redrawn from it after every key, so the
// separator vanished under the caret: « 92 159,5 » typed was SAVED 921595,
// « 76,5 » and « 76.5 » both 765; the client form and the collective case's
// « new client » dialog were `type="number"`, and saved 921595 and 765 too.
//
// THE RULE, one definition for every place an amount is typed through the
// field (src/components/AmountField.tsx — the case form's rate-card field, which
// already read and showed an amount this way, widened for the three quantums):
//   · SHOWN, when not being typed in: `fq` / `moneyFigure` (src/utils.ts),
//     « 92 159,50 », « 76,50 »;
//   · TYPED: digits and ONE decimal separator — the comma, or a dot read as
//     the comma, as the list's cell read it — with spaces ignored (the narrow
//     no-break space the shown figure carries included); anything else is
//     refused as it is typed, as that cell refused it; « 1.234,56 » carries two
//     separators and is refused whole (`parseMoneyCell`'s rule for an import
//     cell: never guess which one is the thousands);
//   · what is typed STAYS as typed while the field has focus; the number is
//     what is saved.
//
// Pure: no React, no DOM. The recording is recordings/207c-amount-text.

// Digits and whitespace, then at most one separator followed by more of the
// same. `\s` covers the no-break space (U+00A0) and the narrow one (U+202F).
const TYPED = /^[\d\s]*([.,][\d\s]*)?$/;
// 216 addendum 6 §10 (4) [RULED 82] — AND A LEADING MINUS, WHERE THE HOST SAYS
// THE DOCUMENT IS A REIMBURSEMENT (`negative`): a repayment is money going out
// and is recorded as a negative payment (161 §6); the window pre-filled it but
// the field refused the sign, so a partial repayment could not be typed. Only
// there: every other amount field keeps the rule above, and a minus typed in
// one is refused as it was.
const TYPED_SIGNED = /^-?[\d\s]*([.,][\d\s]*)?$/;

/** May this text stand in an amount field while it is being typed? A leading
    minus only where `negative` says the amount may be one. */
export const amountTyped = (text: string, negative = false): boolean => (negative ? TYPED_SIGNED : TYPED).test(text);

/** The number a typed amount means: spaces ignored, the comma (or a dot) the
    decimal separator; nothing typed — or a separator alone, or a minus alone —
    is no amount. */
export const parseAmount = (text: string, negative = false): number | null => {
  if (!amountTyped(text, negative)) return null;
  const bare = text.replace(/\s/g, "").replace(",", ".");
  if (bare === "" || bare === "." || bare === "-" || bare === "-.") return null;
  const n = Number(bare);
  return Number.isFinite(n) ? n : null;
};

/** A stored amount as a number, or null when nothing is stored. */
export const amountValue = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** 208 §10 — ONE KEYSTROKE THROUGH THE AMOUNT FIELD: the text the field leaves
    on screen and the amount it hands up, or null when the key is refused. A
    ceiling (`max` — the pro forma's source draw and refund, as `NumField`
    clamped them, 161B §2) holds the amount at it and SHOWS it at once, never
    only when the field is left — IN CENTS, ROUNDED DOWN: a refund's ceiling is
    a VAT division (749,50 € paid ÷ 1,21 is 619,42148…), shown « 619,42 » and
    handed 619,42, never a cent above what it may be. */
export const amountKeystroke = (text: string, max?: number, negative = false): { text: string; amount: number | null } | null => {
  if (!amountTyped(text, negative)) return null;
  const n = parseAmount(text, negative);
  if (max !== undefined && n !== null && n > max) {
    const cap = Math.floor(max * 100 + 1e-9) / 100; // + 1e-9: 0,29 × 100 is 28,999…96 in binary
    return { text: String(cap).replace(".", ","), amount: cap };
  }
  return { text, amount: n };
};
